import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { buddyService } from '../services/buddyService';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { userProfileService } from '../services/userProfileService';
import { reportHomeRegion } from '../utils/regionSync';

const inFlight = new Set<string>();

/**
 * Renders nothing. Once per browser session, for a signed-in diver, reports which region their home site is in
 * (see reportHomeRegion). This is how divers who predate the server's region index get listed as locals of their
 * region without anyone running a migration; it also repairs any membership row that was missed. It also reports the
 * browser's time zone (also once per session), so reminder emails can show event times in the diver's own zone.
 */
const RegionIndexSync: React.FC = () => {
  const location = useLocation(); // re-check after navigating, so signing in mid-session is picked up
  const userId = authService.getUser()?.userId ?? null;
  const { allSites, loading } = useDiveSiteCatalog();

  useEffect(() => {
    if (!userId || loading || allSites.length === 0 || inFlight.has(userId)) return;
    const key = `regionIndexSync:${userId}`;
    try { if (sessionStorage.getItem(key)) return; } catch { /* storage blocked: sync each load instead */ }

    inFlight.add(userId);
    buddyService.getUserProfile(userId)
      .then(profile => reportHomeRegion(profile, allSites))
      .then(() => { try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ } })
      .catch(() => { /* best-effort; the next session tries again */ })
      .finally(() => inFlight.delete(userId));
  }, [userId, loading, allSites, location.pathname]);

  // Independent of the dive-site catalog, so it isn't held up waiting for it.
  useEffect(() => {
    if (!userId) return;
    const key = `timeZoneSync:${userId}`;
    try { if (sessionStorage.getItem(key)) return; } catch { /* storage blocked: report each load instead */ }
    const zone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } })();
    if (!zone) return;
    userProfileService.setTimeZone(zone)
      .then(() => { try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ } })
      .catch(() => { /* best-effort; the next session tries again */ });
  }, [userId, location.pathname]);

  return null;
};

export default RegionIndexSync;
